import "./styles.css";
import { ptBR } from "date-fns/locale";
import Timeline, { DateHeader, TimelineHeaders } from "react-calendar-timeline";
import { useEffect, useRef, useState } from "react";
import { Button, Radio, Skeleton } from "antd";
import {
  addDays,
  addWeeks,
  endOfWeek,
  format,
  getTime,
  setHours,
  startOfDay,
  startOfWeek,
} from "date-fns";
import { formatSeniority } from "../../../../utils/helpers/ticketHelpers";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

interface AgendaTimelineProps {
  events: any[];
  groups: { id: number; title: string; seniorityId: number }[];
  defaultAtualView?: string;
  defaultTimeRange?: { start: number; end: number };
  enableItemMove?: boolean;
  enableGroupChange?: boolean;
  enableItemResize?: boolean;
  onEventMove?: (
    itemId: number | string,
    dragTime: number,
    newGroupOrder: number,
    view: string
  ) => void;
  onEventResize?: (
    itemId: number | string,
    time: number,
    edge?: "left" | "right"
  ) => void;
  onEventClick?: (eventId: number | string, time: number) => void;
  onEventDoubleClick?: (
    eventId: number | string,
    ticketId: any,
    time: number,
    groupOrder: number
  ) => void;
  onSetTimeRange?: (timeRange: any) => void;
  onDrop?: (event: any) => void;
  onSetAtualView?: (view: string) => void;
}

const hasSameTimeRange = (
  left?: { start: number; end: number },
  right?: { start: number; end: number }
) => left?.start === right?.start && left?.end === right?.end;

const ticketTypeLegend = [
  { label: "Linha Pronta", color: "#01c558" },
  { label: "Promob", color: "#722ed1" },
  { label: "Assistência Técnica", color: "#faad14" },
  { label: "Avulso", color: "#1890ff" },
  {
    label: "Em montagem",
    color: "#ffffff",
    borderColor: "#059669",
  },
  {
    label: "Promob com Assistência Técnica",
    color: "#722ed1",
    borderColor: "#faad14",
  },
];

const AgendaTimeline = ({
  events,
  groups,
  defaultAtualView,
  defaultTimeRange,
  enableItemMove,
  enableGroupChange,
  enableItemResize,
  onEventDoubleClick,
  onEventClick,
  onEventMove,
  onEventResize,
  onDrop,
  onSetTimeRange,
  onSetAtualView,
}: AgendaTimelineProps) => {
  const [atualView, setAtualView] = useState(defaultAtualView || "week");

  const [timeRange, setTimeRange] = useState({
    start: defaultTimeRange?.start || startOfWeek(new Date()).getTime(),
    end: defaultTimeRange?.end || endOfWeek(new Date()).getTime(),
  });

  const [formatedEvents, setFormatedEvents] = useState<any[]>([]);
  const lastEmittedTimeRangeRef = useRef(timeRange);

  const setNextDayView = (day: number) => {
    setTimeRange({
      start: addDays(timeRange.start, day).getTime(),
      end: addDays(timeRange.end, day).getTime(),
    });
  };

  const setNextWeekView = (week: number) => {
    setTimeRange({
      start: addWeeks(timeRange.start, week).getTime(),
      end: addWeeks(timeRange.end, week).getTime(),
    });
  };

  const setDayView = (day: number) => {
    if (atualView === "day") return;

    setTimeRange({
      start: setHours(startOfDay(day), 6).getTime(),
      end: setHours(startOfDay(day), 22).getTime(),
    });
    setAtualView("day");
  };

  const setWeekView = (atualView: any) => {
    if (atualView === "week") return;

    setTimeRange({
      start: startOfWeek(timeRange.start).getTime(),
      end: endOfWeek(timeRange.end).getTime(),
    });
    setAtualView("week");
  };

  const getColorBySeniority = (seniorityId: number) => {
    switch (seniorityId) {
      case 1:
        return "#015f2a";
      case 2:
        return "#01c558";
      case 3:
        return "#2efe8a";
      default:
        return "#ed9a9a";
    }
  };

  useEffect(() => {
    if (atualView === "week") {
      setWeekView(atualView);
    }

    setFormatedEvents(events.map((event) => ({ ...event })));
  }, [events]);

  useEffect(() => {
    if (!defaultTimeRange) {
      return;
    }

    setTimeRange((currentTimeRange) =>
      hasSameTimeRange(currentTimeRange, defaultTimeRange)
        ? currentTimeRange
        : defaultTimeRange
    );
  }, [defaultTimeRange?.start, defaultTimeRange?.end]);

  useEffect(() => {
    if (!onSetTimeRange) {
      return;
    }

    if (hasSameTimeRange(lastEmittedTimeRangeRef.current, timeRange)) {
      return;
    }

    lastEmittedTimeRangeRef.current = timeRange;
    onSetTimeRange(timeRange);
  }, [timeRange]);

  useEffect(() => {
    if (onSetAtualView) onSetAtualView(atualView);
  }, [atualView]);

  return (
    <div className="flex w-full flex-col items-center justify-between mt-16 mb-5 px-4 py-3 bg-white shadow-md rounded-xl gap-3">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <Radio.Group
            buttonStyle="solid"
            value={atualView}
            onChange={(e) =>
              e.target.value === "week"
                ? setWeekView(atualView)
                : setDayView(getTime(timeRange.start))
            }
          >
            <Radio.Button value="week">Semana</Radio.Button>
            <Radio.Button value="day">Dia</Radio.Button>
          </Radio.Group>
        </div>

        <div className="flex items-center gap-3 pl-[150px]">
          <Button
            type="primary"
            shape="circle"
            size="middle"
            icon={<LeftOutlined />}
            onClick={() =>
              atualView === "day" ? setNextDayView(-1) : setNextWeekView(-1)
            }
          />
          <Button
            type="primary"
            shape="round"
            size="middle"
            className="px-5 font-medium"
            onClick={() => {
              setDayView(getTime(timeRange.start));
              setTimeRange({
                start: setHours(startOfDay(new Date().getTime()), 6).getTime(),
                end: setHours(startOfDay(new Date().getTime()), 22).getTime(),
              });
            }}
          >
            Hoje
          </Button>
          <Button
            type="primary"
            shape="circle"
            size="middle"
            icon={<RightOutlined />}
            onClick={() =>
              atualView === "day" ? setNextDayView(1) : setNextWeekView(1)
            }
          />
        </div>

        <div className="w-[140px]" />
      </div>

      <div className="flex w-full flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-700">
        <span className="font-semibold text-gray-800">Legenda:</span>
        {ticketTypeLegend.map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-8 rounded-sm"
              style={{
                backgroundColor: item.color,
                border: item.borderColor
                  ? `3px solid ${item.borderColor}`
                  : "1px solid rgba(0, 0, 0, 0.12)",
              }}
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      <div
        onDrop={(event) => onDrop?.(event)}
        onDragOver={(e) => e.preventDefault()}
        style={{ width: "100%", height: "100%" }}
      >
        {groups.length ? (
          <Timeline
            canChangeGroup={enableGroupChange ?? Boolean(enableItemMove)}
            itemTouchSendsClick={false}
            stackItems
            itemHeightRatio={0.75}
            canMove={Boolean(enableItemMove)}
            onItemDrag={(event) => {
              console.log(event);
            }}
            onItemResize={(itemId, time, edge) => {
              onEventResize?.(itemId, time, edge);
            }}
            canResize={enableItemResize ? "both" : false}
            useResizeHandle={Boolean(enableItemResize)}
            groups={groups}
            items={formatedEvents}
            visibleTimeStart={timeRange.start}
            visibleTimeEnd={timeRange.end}
            onItemMove={(itemId, dragTime, newGroupOrder) => {
              const newGroup = groups[newGroupOrder];
              if (!newGroup) return;
              onEventMove?.(itemId, dragTime, newGroup.id, atualView);
            }}
            onItemDoubleClick={(eventId, htmlEvent, time) => {
              const event = events.find((item) => item.id === Number(eventId));
              if (!event) {
                return;
              }
              onEventDoubleClick?.(
                eventId,
                event.fieldReportId,
                event.start_time,
                event.assemblerId
              );
            }}
            onItemClick={(eventId, htmlEvent, time) =>
              onEventClick?.(eventId, time)
            }
            timeSteps={{
              second: 1,
              minute: 15,
              hour: 1,
              day: 1,
              month: 1,
              year: 1,
            }}
            groupRenderer={({ group }) => {
              return (
                <div
                  style={{
                    borderLeft:
                      "4px solid " + getColorBySeniority(group.seniorityId),
                  }}
                  className="pl-2 "
                >
                  {group.title}
                </div>
              );
            }}
            itemRenderer={({
              item,
              itemContext,
              getItemProps,
              getResizeProps,
            }) => {
              const { left: leftResizeProps, right: rightResizeProps } =
                getResizeProps({
                  leftStyle: {
                    width: 2,
                    minWidth: 2,
                    maxWidth: 2,
                    cursor: "ew-resize",
                    zIndex: 100,
                  },
                  rightStyle: {
                    width: 2,
                    minWidth: 2,
                    maxWidth: 2,
                    cursor: "ew-resize",
                    zIndex: 100,
                  },
                });

              return (
                <div
                  {...getItemProps({})}
                  key={item.id}
                  className={
                    `rct-item ${formatSeniority(
                      item.seniorityId
                    )?.toLowerCase()}-bg ` + item?.className
                  }
                >
                  {itemContext.useResizeHandle ? (
                    <div {...leftResizeProps} />
                  ) : null}

                  <div
                    className="rct-item-content"
                    style={{
                      maxHeight: itemContext.dimensions.height,
                      pointerEvents: "none",
                      paddingLeft: itemContext.useResizeHandle ? 2 : 8,
                      paddingRight: itemContext.useResizeHandle ? 2 : 8,
                      width: "100%",
                    }}
                  >
                    {atualView === "day" &&
                      item.title.split("|")[0].replace(/\D+/, "")}
                  </div>

                  {itemContext.useResizeHandle ? (
                    <div {...rightResizeProps} />
                  ) : null}
                </div>
              );
            }}
            // groupRenderer={({ group }) => {
            //   return <div>{group.title}</div>;
            // }}
            // lineHeight={50}
          >
            <TimelineHeaders className="sticky top-0 z-10 ">
              {/* <DateHeader unit="primaryHeader" /> */}
              {atualView === "week" && (
                <>
                  <DateHeader
                    unit="week"
                    className="rct-dateHeader text-center font-bold text-lg"
                    labelFormat={(interval) =>
                      format(interval[0].toDate(), "MMMM", { locale: ptBR })
                    }
                    intervalRenderer={({
                      intervalContext,
                      getIntervalProps,
                    }: any) => {
                      if (!getIntervalProps || !intervalContext) return null;
                      const { intervalText } = intervalContext;
                      return (
                        <span {...getIntervalProps()}>
                          {intervalText.toLocaleUpperCase()}
                        </span>
                      );
                    }}
                  />
                  <DateHeader
                    unit="day"
                    className="rct-dateHeader text-center items-center flex pb-2 "
                    labelFormat={(interval) =>
                      format(interval[0].toDate(), "EEE - dd", { locale: ptBR })
                    }
                    intervalRenderer={({
                      intervalContext,
                      getIntervalProps,
                    }: any) => {
                      if (!getIntervalProps || !intervalContext) return null;
                      const { intervalText } = intervalContext;
                      const formated =
                        String(intervalText).charAt(0).toUpperCase() +
                        String(intervalText).slice(1);
                      const splited = formated.split(" - ");
                      return (
                        <span {...getIntervalProps()}>
                          {<div className="text-xs">{splited[0]}</div>}{" "}
                          {<div className="font-bold">{splited[1]}</div>}
                        </span>
                      );
                    }}
                  />
                  {/* <DateHeader
                    unit="hour"
                    className="rct-dateHeader text-center text-[10px] flex gap-1"
                    labelFormat="HH"
                    intervalRenderer={({
                      intervalContext,
                      getIntervalProps,
                    }: any) => {
                      if (!getIntervalProps || !intervalContext) return null;
                      const { intervalText } = intervalContext;
                      console.log(Number(intervalText));
                      if (Number(intervalText) < 6 || Number(intervalText) > 21)
                        return null;
                      return (
                        <span {...getIntervalProps()}>
                          {intervalText.toLocaleUpperCase()}
                        </span>
                      );
                    }}
                  /> */}
                </>
              )}
              {atualView === "day" && (
                <>
                  <DateHeader
                    unit="day"
                    className="rct-dateHeader  left-[190px] text-center font-bold text-lg pb-2 "
                    labelFormat={(interval) =>
                      format(interval[0].toDate(), "MMMM", { locale: ptBR })
                    }
                    intervalRenderer={({
                      intervalContext,
                      getIntervalProps,
                    }: any) => {
                      if (!getIntervalProps || !intervalContext) return null;
                      const { intervalText } = intervalContext;
                      return (
                        <span {...getIntervalProps()}>
                          {intervalText.toLocaleUpperCase()}
                        </span>
                      );
                    }}
                  />
                  <DateHeader
                    unit="day"
                    labelFormat={(interval) =>
                      format(interval[0].toDate(), "EEE - dd", { locale: ptBR })
                    }
                    className="rct-dateHeader text-center left-[190px] pb-2"
                    intervalRenderer={({
                      intervalContext,
                      getIntervalProps,
                    }: any) => {
                      if (!getIntervalProps || !intervalContext) return null;
                      const { intervalText } = intervalContext;
                      const formated =
                        String(intervalText).charAt(0).toUpperCase() +
                        String(intervalText).slice(1);
                      const splited = formated.split(" - ");
                      return (
                        <span {...getIntervalProps()}>
                          {<div className="text-md">{splited[0]}</div>}{" "}
                          {
                            <div className="font-bold text-lg">
                              {splited[1]}
                            </div>
                          }
                        </span>
                      );
                    }}
                  />
                  <DateHeader
                    unit="hour"
                    labelFormat={(interval) =>
                      format(interval[0].toDate(), "HH:mm", { locale: ptBR })
                    }
                    className="rct-dateHeader text-center pb-2"
                    intervalRenderer={({
                      intervalContext,
                      getIntervalProps,
                    }: any) => {
                      if (!getIntervalProps || !intervalContext) return null;
                      const { intervalText } = intervalContext;
                      return (
                        <span {...getIntervalProps()}>
                          {intervalText.toLocaleUpperCase()}
                        </span>
                      );
                    }}
                  />
                </>
              )}
            </TimelineHeaders>
          </Timeline>
        ) : (
          <Skeleton.Node
            active
            style={{ width: "100vw", height: "100vh" }}
            children={[]}
          />
        )}
      </div>
    </div>
  );
};

export default AgendaTimeline;
